import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../../components/AppIcon';
import AnalysisToolbar from './AnalysisToolbar';
import AnalysisDisplayControls from './AnalysisDisplayControls';
import AnalysisKpiGrid from './AnalysisKpiGrid';
import AnalysisCharts from './AnalysisCharts';
import ScenarioComparePanel from './ScenarioComparePanel';
import { getMetricById, buildDynamicMetrics } from './analysisMetrics';
import { getScenarioLabel, mergeProjectionsByYear } from './analysisCompareUtils';
import { loadAnalysisDisplayPrefs, saveAnalysisDisplayPrefs } from './analysisDisplayPrefs';
import InsuranceStrategyGuide from '../InsuranceStrategyGuide';
import CorporateTaxAccountsGuide from '../CorporateTaxAccountsGuide';
import AnalysisDebugTable from './AnalysisDebugTable';
import PlanningHorizonControls from '../PlanningHorizonControls';

const AnalysisTab = ({
  scenarioAId,
  scenarioBId,
  onScenarioAChange,
  onScenarioBChange,
  projectionA,
  projectionB,
  scenarios = [],
  activeScenarioId = null,
  planningHorizonYears = 25,
  planningDeathYear,
  onHorizonChange,
  onDeathYearChange,
  currentYear,
  analysisAssetsA = [],
  analysisAssetsB = [],
  analysisEntitiesA = [],
  analysisEntitiesB = [],
  formatCurrency,
  onGoToPlanning,
}) => {
  const [prefs, setPrefs] = useState(() => loadAnalysisDisplayPrefs());
  const [yearIndex, setYearIndex] = useState(0);

  const rowsA = projectionA?.data || [];
  const rowsB = projectionB?.data || [];
  const compareEnabled = scenarioAId !== scenarioBId;

  useEffect(() => {
    saveAnalysisDisplayPrefs(prefs);
  }, [prefs]);

  useEffect(() => {
    const len = Math.max(rowsA.length, rowsB.length);
    if (len === 0) return;
    setYearIndex((i) => Math.min(i, len - 1));
  }, [rowsA.length, rowsB.length, scenarioAId, scenarioBId]);

  const labelA = getScenarioLabel(scenarioAId, scenarios, activeScenarioId);
  const labelB = getScenarioLabel(scenarioBId, scenarios, activeScenarioId);

  const bundleA = projectionA;
  const primaryBundle = bundleA?.data?.length ? bundleA : projectionB;

  const visibleMetrics = useMemo(() => {
    let selected = prefs.visibleMetricIds
      .map((id) => getMetricById(id, primaryBundle))
      .filter(Boolean);

    if (prefs.showDynamicAssets) {
      selected = [...selected, ...buildDynamicMetrics(primaryBundle).filter((m) => m.id.startsWith('asset::'))];
    }
    if (prefs.showDynamicRevenues) {
      selected = [...selected, ...buildDynamicMetrics(primaryBundle).filter((m) => m.id.startsWith('rev::'))];
    }

    const seen = new Set();
    return selected.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [prefs, primaryBundle]);

  const rowA = rowsA[yearIndex] || rowsA[rowsA.length - 1] || {};
  const lastA = rowsA[rowsA.length - 1] || {};
  const ctxA = { firstRow: rowsA[0], lastRow: lastA };

  const projectionRows = rowsA.length ? rowsA : rowsB;
  const hasData = projectionRows.length > 0;
  const compareMerged = useMemo(
    () => (compareEnabled ? mergeProjectionsByYear(rowsA, rowsB) : []),
    [compareEnabled, rowsA, rowsB],
  );

  if (!hasData) {
    return (
      <div className="space-y-6" data-testid="analysis-tab-content">
        <div className="text-center py-16 rounded-xl border border-border bg-card">
          <Icon name="BarChart3" size={40} className="text-primary mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-foreground">Analyse de projection</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Complétez le bilan et la planification pour générer une analyse détaillée et comparer vos scénarios.
          </p>
          {onGoToPlanning && (
            <button
              type="button"
              onClick={onGoToPlanning}
              className="mt-4 text-sm text-primary font-medium hover:underline"
            >
              Aller au bilan
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analysis-tab-content">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
          Analyse de croissance et comparaison
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Indicateurs configurables, projection sur {planningHorizonYears} ans et comparaison A/B.
        </p>
      </div>
      <PlanningHorizonControls
        currentYear={currentYear}
        horizonYears={planningHorizonYears}
        deathYear={planningDeathYear}
        onHorizonChange={onHorizonChange}
        onDeathYearChange={onDeathYearChange}
        compact
      />
      <AnalysisToolbar
        scenarioAId={scenarioAId}
        scenarioBId={scenarioBId}
        onScenarioAChange={onScenarioAChange}
        onScenarioBChange={onScenarioBChange}
        scenarios={scenarios}
        activeScenarioId={activeScenarioId}
        yearIndex={yearIndex}
        onYearIndexChange={setYearIndex}
        projectionRows={projectionRows}
        planningHorizonYears={planningHorizonYears}
        planningDeathYear={planningDeathYear}
        onGoToPlanning={onGoToPlanning}
        compareEnabled={compareEnabled}
      />
      <InsuranceStrategyGuide />
      <CorporateTaxAccountsGuide />
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="analysis-debug-section">
        <button
          type="button"
          onClick={() => setPrefs((p) => ({ ...p, debugTablesOpen: !p.debugTablesOpen }))}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30"
          data-testid="analysis-debug-toggle"
        >
          <span className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Table2" size={16} className="text-primary" />
            Débogage (tableaux Analyse)
          </span>
          <Icon
            name={prefs.debugTablesOpen ? 'ChevronUp' : 'ChevronDown'}
            size={18}
            className="text-muted-foreground shrink-0"
          />
        </button>
        {prefs.debugTablesOpen && (
          <div className="px-4 pb-4 border-t border-border pt-3">
            <AnalysisDebugTable
              projectionA={rowsA}
              projectionB={rowsB}
              labelA={labelA}
              labelB={labelB}
              compareEnabled={compareEnabled}
              assetsA={analysisAssetsA}
              assetsB={analysisAssetsB}
              entitiesA={analysisEntitiesA}
              entitiesB={analysisEntitiesB}
              formatCurrency={formatCurrency}
            />
          </div>
        )}
      </div>
      <AnalysisDisplayControls
        open={prefs.controlsOpen}
        onToggleOpen={() => setPrefs((p) => ({ ...p, controlsOpen: !p.controlsOpen }))}
        visibleMetricIds={prefs.visibleMetricIds}
        onVisibleMetricIdsChange={(ids) => setPrefs((p) => ({ ...p, visibleMetricIds: ids }))}
        showDynamicAssets={prefs.showDynamicAssets}
        showDynamicRevenues={prefs.showDynamicRevenues}
        onShowDynamicAssetsChange={(v) => setPrefs((p) => ({ ...p, showDynamicAssets: v }))}
        onShowDynamicRevenuesChange={(v) => setPrefs((p) => ({ ...p, showDynamicRevenues: v }))}
        bundle={primaryBundle}
        activeChartTab={prefs.activeChartTab}
        onActiveChartTabChange={(tab) => setPrefs((p) => ({ ...p, activeChartTab: tab }))}
      />
      <AnalysisKpiGrid
        metrics={visibleMetrics}
        row={rowA}
        bundle={bundleA}
        ctx={ctxA}
        formatCurrency={formatCurrency}
        title={`Indicateurs — ${labelA} (année ${rowA.year ?? '—'})`}
      />
      {!compareEnabled ? (
        <AnalysisCharts
          projection={rowsA}
          bundle={bundleA}
          assetNames={bundleA?.assetNames || []}
          revenueNames={bundleA?.revenueNames || []}
          activeChartTab={prefs.activeChartTab}
          formatCurrency={formatCurrency}
          yearIndex={yearIndex}
        />
      ) : (
        <>
          <div className="rounded-lg border border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm text-foreground">
            Comparaison active : <strong style={{ color: 'var(--color-primary)' }}>{labelA}</strong> versus{' '}
            <strong style={{ color: '#f59e0b' }}>{labelB}</strong>. Les écarts favorables à B sont en vert.
          </div>
          <ScenarioComparePanel
            projectionA={projectionA}
            projectionB={projectionB}
            labelA={labelA}
            labelB={labelB}
            visibleMetrics={visibleMetrics}
            yearIndex={yearIndex}
            formatCurrency={formatCurrency}
          />
          <AnalysisCharts
            projection={rowsA}
            bundle={bundleA}
            assetNames={bundleA?.assetNames || []}
            revenueNames={bundleA?.revenueNames || []}
            activeChartTab={prefs.activeChartTab}
            formatCurrency={formatCurrency}
            yearIndex={yearIndex}
            compareMergedData={compareMerged}
            labelA={labelA}
            labelB={labelB}
          />
        </>
      )}
    </div>
  );
};

export default AnalysisTab;

